import {Button} from '@wandb/weave/components/Button/Button';
import {Loading} from '@wandb/weave/components/Loading';
import {CompareEvaluationsControlBar} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/components/CompareEvaluationsControlBar';
import {useEntityProject} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/context';
import {
  useAvailableDatasets,
  useSelectedModelsCount,
} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/hooks/useLeaderboardComparison';
import {useLeaderboardNavigation} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/hooks/useLeaderboardNavigation';
import {useCallsSummaryContext} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/CallsPage/CallsSummaryContext';
import {getAllEvaluationRecords} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/LeaderboardPage/LeaderboardPage';
import {LeaderboardGrid} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/views/Leaderboard/LeaderboardGrid';
import {useLeaderboardData} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/views/Leaderboard/query/hookAdapters';
import {FilterAndGroupSourceEvaluationSpec} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/views/Leaderboard/types/leaderboardConfigType';
import React, {useMemo, useState} from 'react';

import {stringIsRef} from '../pages/wfInterface/naive';
import {
  EXPANDED_REF_REF_KEY,
  isExpandedRefWithValue,
} from '../pages/wfReactInterface/tsDataModelHooksCallRefExpansion';
import {callsChartsStyles} from './CallsCharts.styles';
import {ExtractedCallData} from './types';

const getVisibleSourceEvaluations = (
  data: ExtractedCallData[],
  visibleCallIds: string[]
) => {
  // Get a list containing full call for all visible call ids
  const visibleCalls = data.filter(c => c.callId in visibleCallIds);
  const evalRefs = new Set<FilterAndGroupSourceEvaluationSpec>();
  for (const c of visibleCalls) {
    const evalRef = c.inputs?.self;
    if (!evalRef) {
      continue;
    }

    let refStr;

    if (
      evalRef &&
      typeof evalRef === 'object' &&
      isExpandedRefWithValue(evalRef)
    ) {
      refStr = evalRef[EXPANDED_REF_REF_KEY];
    } else if (evalRef && typeof evalRef === 'string' && stringIsRef(evalRef)) {
      refStr = evalRef;
    }

    if (!refStr) {
      continue;
    }
    const [, prefix, suffix] = refStr.split(':');

    const versionDigest = suffix;
    const name = prefix.substring(prefix.lastIndexOf('/') + 1);

    evalRefs.add({
      name,
      version: versionDigest,
    });
  }
  return Array.from(evalRefs.keys());
};

export const EvalsLeaderboard: React.FC<{
  visibleCallIds?: string[];
  loading?: boolean;
}> = ({visibleCallIds: propVisibleCallIds, loading: propLoading = false}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedEvaluations, setSelectedEvaluations] = useState<string[]>([]);
  const {entity, project} = useEntityProject();
  const ctx = useCallsSummaryContext();

  // Use props if provided, otherwise fall back to context
  const visibleCallIds = propVisibleCallIds ?? ctx?.visibleCallIds;

  // Early return with empty state if no visible calls
  const hasVisibleCalls = visibleCallIds && visibleCallIds.length > 0;

  const sourceEvaluations: FilterAndGroupSourceEvaluationSpec[] =
    useMemo(() => {
      if (!ctx?.data || !hasVisibleCalls) {
        return [];
      }
      return getVisibleSourceEvaluations(ctx.data, ctx.visibleCallIds);
    }, [ctx, hasVisibleCalls]);

  const spec = {sourceEvaluations};
  const callIds = hasVisibleCalls ? visibleCallIds : undefined;

  const {data, loading: dataLoading} = useLeaderboardData(
    entity,
    project,
    spec,
    callIds
  );

  const allRecords = useMemo(() => {
    if (!data) {
      return [];
    }
    return getAllEvaluationRecords(data);
  }, [data]);

  // Calculate the number of selected models (rows) based on selectedEvaluations
  const selectedModelsCount = useSelectedModelsCount(
    data,
    allRecords,
    selectedEvaluations
  );

  // Calculate available datasets based on selected evaluations
  const availableDatasets = useAvailableDatasets(
    data,
    allRecords,
    selectedEvaluations
  );

  // Handle navigation to comparison view with dataset filtering
  const {navigateToComparison} = useLeaderboardNavigation(
    entity,
    project,
    selectedEvaluations,
    availableDatasets,
    allRecords
  );

  if (dataLoading || propLoading) {
    return (
      <div className="tw-style flex h-full w-full flex-col overflow-hidden bg-white pt-24">
        <Loading centered />
      </div>
    );
  }

  if (!hasVisibleCalls) {
    return null;
  }

  return (
    <div className="bg-white border-b border-moon-250">
      <div className={callsChartsStyles.header}>
        <div className={callsChartsStyles.headerContent}>
          <div className={callsChartsStyles.headerLeft}>
            <Button
              variant="ghost"
              size="small"
              icon={isCollapsed ? 'chevron-next' : 'chevron-down'}
              tooltip={
                isCollapsed ? 'Expand leaderboard' : 'Collapse leaderboard'
              }
              onClick={() => setIsCollapsed(prev => !prev)}
            />
            <div className={callsChartsStyles.headerText}>Leaderboard</div>
          </div>
        </div>
      </div>
      {!isCollapsed && (
        <>
          <CompareEvaluationsControlBar
            entity={entity}
            project={project}
            selectedEvaluations={selectedEvaluations}
            selectedModelsCount={selectedModelsCount}
            availableDatasets={availableDatasets}
            loading={dataLoading}
            onClearSelection={() => setSelectedEvaluations([])}
            onDatasetSelect={navigateToComparison}
          />
          <div className="-mt-1">
            <LeaderboardGrid
              entity={entity}
              project={project}
              data={data}
              loading={false}
              allRecords={allRecords}
              selectedEvaluations={selectedEvaluations}
              onSelectedEvaluationsChange={setSelectedEvaluations}
            />
          </div>
        </>
      )}
    </div>
  );
};
